import {
  OrderType,
  QBankStructureStatus,
  useSearchQBankStructuresLazyQuery,
} from '@exam-management/graphql';
import { type OptionSelectionProps } from '@shared/components';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

interface QBS {
  id: string;
  code: number;
  name: string;
}

const toOption = (qbs: QBS): OptionSelectionProps => ({
  label: `${qbs.code}: ${qbs.name}`,
  value: qbs.id,
});

/**
 * QBank options for the keyword-search filter dropdown.
 * Server-side `name` search + page-by-page accumulation (infinite scroll),
 * so we never load the whole catalogue up front.
 */
export default function useQBankOptions() {
  const [load] = useSearchQBankStructuresLazyQuery({ fetchPolicy: 'network-only' });

  const [items, setItems] = useState<QBS[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(0); // which page is currently in flight
  const [called, setCalled] = useState(false);

  const termRef = useRef('');
  const pageRef = useRef(0);
  const reqIdRef = useRef(0); // guards against stale responses racing a newer search

  const fetchPage = useCallback(
    async (term: string, page: number) => {
      const reqId = ++reqIdRef.current;
      setLoading(true);
      setLoadingPage(page);
      setCalled(true);
      try {
        const res = await load({
          variables: {
            // omit `name` entirely when empty so the initial open matches the
            // proven-fast query shape (sending name routes to a slower search path)
            search: {
              status: [QBankStructureStatus.Published],
              ...(term ? { name: term } : {}),
            },
            paging: { currentPage: page, pageSize: PAGE_SIZE },
            order: { field: 'code', type: OrderType.Asc },
          },
        });
        if (reqId !== reqIdRef.current) return; // a newer request superseded this one
        const result = res.data?.qbsSearchQBankStructures;
        const fetched = (result?.qBankStructures ?? []) as QBS[];
        setTotal(result?.total ?? 0);
        pageRef.current = page;
        setItems((prev) => (page === 0 ? fetched : [...prev, ...fetched]));
      } catch {
        // swallow: leave items empty so reopening the dropdown can retry
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    },
    [load],
  );

  const open = useCallback(() => {
    if (!loading && items.length === 0) fetchPage('', 0);
  }, [loading, items.length, fetchPage]);

  // Debounced server search; resets back to page 0.
  const debouncedFetch = useMemo(
    () => debounce((term: string) => fetchPage(term, 0), SEARCH_DEBOUNCE_MS),
    [fetchPage],
  );
  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  const search = useCallback(
    (term: string) => {
      termRef.current = term;
      debouncedFetch(term);
    },
    [debouncedFetch],
  );

  const hasMore = items.length < total;

  // Fetch the next page (called on scroll-to-bottom).
  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    fetchPage(termRef.current, pageRef.current + 1);
  }, [loading, hasMore, fetchPage]);

  const options = useMemo(() => items.map(toOption), [items]);

  const qBankRefIdToQBankCodeMap = useMemo(() => {
    const map: Record<string, number> = {};
    items.forEach((qbs) => {
      map[qbs.id] = qbs.code;
    });
    return map;
  }, [items]);

  const isLoadingFirstPage = !called || (loading && loadingPage === 0);
  const isLoadingMore = loading && loadingPage > 0;

  return {
    options,
    isLoadingFirstPage,
    isLoadingMore,
    hasMore,
    open,
    search,
    loadMore,
    refIdToCodeMap: qBankRefIdToQBankCodeMap,
  };
}
